import { useLayoutEffect, useRef, useSyncExternalStore, type ReactElement, type RefObject } from "react";
import { GlyphButton, PlayFloat, windowRoom, type GlyphName, type PlaceSize } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { HudArea } from "../hud/slots.ts";
import { legendWords, type HudMinimap } from "../minimap/minimap.tsx";
import { windowPlace, windowSize } from "../windows/places.ts";
import { FLOAT_RIM, STRIP_BUTTON, floatHome, floatLegend, floatPanelTop, stripControls, type StripControl } from "./float.ts";
import { legendStrip } from "./nearby.ts";
import { PopOutButton } from "./popout-button.tsx";
import type { MapPopout } from "./popout-host.ts";
import type { WorldMapSnapshot } from "./store.ts";
import { LevelView, useMapLevels, useZoomInput, type HudWorldMap, type WorldMapControl } from "./window.tsx";

/**
 * The float (P2.12-T23, ADR-100's amendment of 2026-10-14, decisions 48, 49 and 51): the Map shown as a frameless, always
 * on top live map in the page, over the game. It draws the window's body at its own size from the one store, so the level,
 * the pan, the pin and the Places panel are the window's: Float and Dock only change which of the two is drawn. It is a
 * `PlayFloat` of the kit (id `mapFloat`, its place and size kept as the frame keeps them), so it is dragged by its
 * strip, resized from its rim and its grip and never off the page. At Nearby the field fills the box and the legend
 * stands under it where it fits (`floatLegend`); at Region the picture, the arrow and the marks do, with Places as the
 * sheet over the lower map, since the strip holds its button. It is not a play window of the windows' order, not a layer of
 * the input router and names no key, so every key reaches the game; the wheel over it and a two finger pinch step the
 * levels as in the window.
 */

/**
 * The glyph of each strip control, from the kit's atlas.
 */
const STRIP_GLYPHS: Readonly<Record<StripControl, GlyphName>> = {
  zoomOut: "minus",
  zoomIn: "plus",
  places: "book",
  centre: "bullseye",
  clearPin: "unlink",
  dock: "dock",
  popOut: "popout",
  close: "close",
};

/**
 * The controls the level offers: Places, Centre and Clear pin are Region's, as they are the window's.
 *
 * @param snapshot The store's snapshot.
 * @param popout Whether the page can open the pop-out window.
 * @returns The controls.
 */
function offered(snapshot: WorldMapSnapshot, popout: boolean): readonly StripControl[] {
  const own: readonly StripControl[] = popout ? ["dock", "popOut", "close"] : ["dock", "close"];
  return snapshot.level === "region" ? ["zoomOut", "zoomIn", "places", "centre", "clearPin", ...own] : ["zoomOut", "zoomIn", ...own];
}

/**
 * One strip button: a 16 pixel glyph button named for a screen reader, which keeps the game's keys when pressed.
 *
 * @param props The `control`, the `store` it acts on, its `snapshot` and the `popout` that opens the pop-out window.
 * @returns The button.
 */
function StripButton({ control, store, snapshot, popout }: { readonly control: StripControl; readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly popout: MapPopout | undefined }): ReactElement | null {
  const glyph = STRIP_GLYPHS[control];
  const data = { "data-worldmap": `float-${control}` } as const;
  switch (control) {
    case "zoomOut":
      return <GlyphButton glyph={glyph} label={say("worldMap.zoomOut")} size={STRIP_BUTTON} keepFocus disabled={!snapshot.canZoomOut} onClick={() => store.zoomOut()} data={data} />;
    case "zoomIn":
      return <GlyphButton glyph={glyph} label={say("worldMap.zoomIn")} size={STRIP_BUTTON} keepFocus disabled={!snapshot.canZoomIn} onClick={() => store.zoomIn()} data={data} />;
    case "places":
      return <GlyphButton glyph={glyph} label={say("worldMap.places")} size={STRIP_BUTTON} keepFocus expanded={snapshot.panelOpen} onClick={() => store.togglePanel()} data={data} />;
    case "centre":
      return <GlyphButton glyph={glyph} label={say("worldMap.centre")} size={STRIP_BUTTON} keepFocus disabled={snapshot.following} onClick={() => store.centre()} data={data} />;
    case "clearPin":
      return <GlyphButton glyph={glyph} label={say("worldMap.clearPin")} size={STRIP_BUTTON} keepFocus disabled={snapshot.pin === null} onClick={() => store.clearPin()} data={data} />;
    case "dock":
      return <GlyphButton glyph={glyph} label={say("worldMap.dock")} size={STRIP_BUTTON} keepFocus onClick={() => store.dock()} data={data} />;
    case "popOut":
      return popout === undefined ? null : <PopOutButton store={store} popout={popout} size={STRIP_BUTTON} name={`float-${control}`} />;
    case "close":
      return <GlyphButton glyph={glyph} label={say("worldMap.close")} size={STRIP_BUTTON} keepFocus onClick={() => store.close()} data={data} />;
  }
}

/**
 * The float's body: the level's view at the float's size, which the store takes as its view, under the wheel and the
 * pinch of the whole float.
 *
 * @param props The `box` the float is drawn at, the `root` the zoom input listens on (the float's own element), the
 *   `store`, its `snapshot` and the `minimap` Nearby is drawn from.
 * @returns The view.
 */
function FloatBody({ box, root, store, snapshot, minimap }: { readonly box: PlaceSize; readonly root: RefObject<HTMLElement | null>; readonly store: WorldMapControl; readonly snapshot: WorldMapSnapshot; readonly minimap: HudMinimap | undefined }): ReactElement {
  const view = { width: box.width, height: box.height };
  const pinching = useRef(false);
  useLayoutEffect(() => {
    store.setView(view);
  }, [store, view.width, view.height]);
  useZoomInput(root, store, pinching);
  const wanted = legendStrip(view.width, legendWords());
  const strip = floatLegend(view, wanted) ? wanted : 0;
  const { shown, held } = useMapLevels(view, strip, snapshot, minimap);
  return <LevelView shown={shown} held={held} view={view} strip={strip} store={store} snapshot={snapshot} minimap={minimap} controls={null} corner={false} panelTop={floatPanelTop()} pinching={pinching} />;
}

/**
 * The float, while the store says the Map is open and shown as the float (never on the phone layout, which the store
 * answers by showing the window).
 *
 * @param props The `area` it is laid out on, the `map` it reads and acts on and the `minimap` its Nearby level is
 *   drawn from.
 * @returns The float, or nothing.
 */
export function WorldMapFloat({ area, map, minimap }: { readonly area: HudArea; readonly map: HudWorldMap; readonly minimap?: HudMinimap | undefined }): ReactElement | null {
  const store = map.store;
  const popout = map.popout;
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const root = useRef<HTMLDivElement>(null);
  if (!snapshot.open || !snapshot.ready || snapshot.presentation !== "float") {
    return null;
  }
  const room = windowRoom(area);
  return (
    <PlayFloat
      id="mapFloat"
      label={say("worldMap.floatStrip")}
      home={floatHome(room)}
      room={room}
      place={windowPlace("mapFloat")}
      size={windowSize("mapFloat")}
      rim={FLOAT_RIM}
      host={root}
      className="ot-worldmap-float"
      data={{ "data-worldmap": "float", "data-level": snapshot.level }}
      strip={(box) => stripControls({ width: box.width - 2 * FLOAT_RIM, height: box.height }, offered(snapshot, popout !== undefined && popout.available())).map((control) => <StripButton key={control} control={control} store={store} snapshot={snapshot} popout={popout} />)}
    >
      {(box) => <FloatBody box={box} root={root} store={store} snapshot={snapshot} minimap={minimap} />}
    </PlayFloat>
  );
}
